import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowUpRight, ArrowUpDown } from 'lucide-react'
import {
  topCompras,
  categorias,
  diasRestantes,
  urgencia,
} from '../data'
import type { CategoriaFiltro } from '../App'

const urgencyStyles = {
  critical: 'text-terracotta bg-terracotta/10 border-terracotta/30',
  warm: 'text-taupe-dark bg-taupe/15 border-taupe/40',
  steady: 'text-sage bg-sage/10 border-sage/30',
}

type Props = {
  categoria: CategoriaFiltro
  setCategoria: (c: CategoriaFiltro) => void
}

export function TopCompras({ categoria, setCategoria }: Props) {
  const [sort, setSort] = useState<'multi' | 'urg'>('multi')

  const ordered = useMemo(() => {
    const list = [...topCompras]
    const filtered = categoria === 'Todas' ? list : list.filter((c) => c.categoria === categoria)
    return filtered.sort((a, b) => {
      if (sort === 'multi') {
        const ma = a.multiplicadorClube ?? a.multiplicador
        const mb = b.multiplicadorClube ?? b.multiplicador
        return mb - ma
      }
      return diasRestantes(a.validoAte) - diasRestantes(b.validoAte)
    })
  }, [categoria, sort])

  return (
    <section id="compras" className="bg-bone py-16 md:py-24">
      <div className="container-editorial">
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
          <div>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-taupe" />
              <span className="text-xs tracking-[0.35em] uppercase text-taupe-dark font-medium">
                Top 10 compras
              </span>
            </div>
            <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] tracking-tighter text-ink">
              Onde vale a pena <em className="text-taupe-dark">comprar agora</em>.
            </h2>
            <p className="mt-3 text-base text-ink/60 max-w-xl">
              Lojas parceiras ordenadas pelo multiplicador de pontos por real.
              Atualizado diariamente a partir das páginas oficiais das operadoras.
            </p>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs tracking-[0.25em] uppercase text-taupe hidden md:inline">Ordenar</span>
            <button
              onClick={() => setSort('multi')}
              aria-pressed={sort === 'multi'}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full transition-colors text-xs tracking-wide ${
                sort === 'multi'
                  ? 'bg-ink text-bone'
                  : 'bg-bone border border-taupe/30 text-ink/70 hover:border-ink'
              }`}
            >
              <ArrowUpDown className="w-3.5 h-3.5" strokeWidth={1.5} />
              Multiplicador
            </button>
            <button
              onClick={() => setSort('urg')}
              aria-pressed={sort === 'urg'}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full transition-colors text-xs tracking-wide ${
                sort === 'urg'
                  ? 'bg-ink text-bone'
                  : 'bg-bone border border-taupe/30 text-ink/70 hover:border-ink'
              }`}
            >
              Urgência
            </button>
          </div>
        </header>

        {/* Filtros de categoria */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 -mx-2 px-2">
          <button
            onClick={() => setCategoria('Todas')}
            aria-pressed={categoria === 'Todas'}
            className={`shrink-0 text-xs tracking-wide px-4 py-2 rounded-full transition-all ${
              categoria === 'Todas'
                ? 'bg-ink text-bone'
                : 'bg-bone border border-taupe/30 text-ink/70 hover:border-ink'
            }`}
          >
            Todas
          </button>
          {categorias.map((c) => {
            const count = topCompras.filter((co) => co.categoria === c.nome).length
            return (
              <button
                key={c.nome}
                onClick={() => setCategoria(c.nome)}
                aria-pressed={categoria === c.nome}
                className={`shrink-0 text-xs tracking-wide px-4 py-2 rounded-full transition-all ${
                  categoria === c.nome
                    ? 'bg-ink text-bone'
                    : 'bg-bone border border-taupe/30 text-ink/70 hover:border-ink'
                }`}
              >
                {c.nome}
                <span className="ml-1.5 text-[10px] opacity-60">·{count}</span>
              </button>
            )
          })}
        </div>

        {/* Tabela */}
        <div className="rounded-2xl border border-taupe/20 overflow-hidden bg-bone">
          {/* Cabeçalho */}
          <div className="hidden md:grid grid-cols-[48px_1.5fr_1fr_0.9fr_0.9fr_100px] gap-4 px-6 py-3 bg-sand/40 text-[10px] tracking-[0.25em] uppercase text-taupe-dark">
            <div>#</div>
            <div>Loja</div>
            <div>Operadora · Categoria</div>
            <div>Multiplicador</div>
            <div>Validade</div>
            <div></div>
          </div>

          <div className="divide-y divide-taupe/15">
            {ordered.length === 0 && (
              <div className="py-16 text-center text-sm text-taupe">
                Nenhuma loja ativa na categoria "{categoria}" no momento. Tente "Todas" ou aguarde o próximo ciclo.
              </div>
            )}
            {ordered.map((c, i) => {
              const dias = diasRestantes(c.validoAte)
              const urg = urgencia(dias)
              const multSemClube = c.multiplicador
              const multClube = c.multiplicadorClube

              return (
                <motion.a
                  key={c.id}
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="group grid md:grid-cols-[48px_1.5fr_1fr_0.9fr_0.9fr_100px] grid-cols-[auto_1fr_auto] gap-4 items-center px-6 py-5 hover:bg-sand/30 transition-colors"
                >
                  <div className="font-sans font-light text-xl text-taupe-dark/60 tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </div>

                  <div className="min-w-0">
                    <div className="font-medium text-ink text-[15px] md:text-base truncate">
                      {c.loja}
                    </div>
                    <div className="md:hidden text-xs text-taupe-dark mt-0.5">
                      {c.operadora} · {c.categoria}
                    </div>
                    {c.obs && (
                      <div className="text-xs text-taupe-dark/80 mt-1 md:line-clamp-2">
                        {c.obs}
                      </div>
                    )}
                  </div>

                  <div className="hidden md:block text-sm">
                    <div className="text-ink/80">{c.operadora}</div>
                    <div className="text-xs text-taupe">{c.categoria}</div>
                  </div>

                  <div className="hidden md:flex flex-col">
                    <div className="font-sans font-semibold text-xl text-ink tabular-nums leading-none">
                      {multSemClube}
                      <span className="text-xs text-taupe-dark font-normal ml-1">
                        pts/R$
                      </span>
                    </div>
                    {multClube && (
                      <div className="text-[11px] text-terracotta mt-1">
                        + Clube: <strong className="font-semibold">{multClube}× </strong>
                      </div>
                    )}
                  </div>

                  <div className="hidden md:flex flex-col">
                    <div className={`inline-flex items-center self-start text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full border ${urgencyStyles[urg]}`}>
                      {dias <= 0 ? 'último dia' : `${dias} dia${dias !== 1 ? 's' : ''}`}
                    </div>
                    <div className="text-xs text-taupe mt-1">
                      até {new Date(c.validoAte + 'T00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                    </div>
                  </div>

                  {/* Mobile: resumo */}
                  <div className="md:hidden text-right">
                    <div className="font-sans font-semibold text-xl text-ink tabular-nums">
                      {multClube ?? multSemClube}×
                    </div>
                    <div className={`text-[10px] mt-0.5 ${urg === 'critical' ? 'text-terracotta' : 'text-taupe'}`}>
                      {dias <= 0 ? 'hoje' : `${dias}d`}
                    </div>
                  </div>

                  <div className="text-ink/40 group-hover:text-ink transition-colors">
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                </motion.a>
              )
            })}
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 mt-6 px-1">
          <p className="text-xs text-taupe">
            {ordered.length} {ordered.length === 1 ? 'loja' : 'lojas'} · dados coletados das páginas oficiais das operadoras · atualização diária
          </p>
          <a href="#entrar" className="text-xs text-ink/70 hover:text-ink inline-flex items-center gap-1.5 group">
            Receber alertas dessa categoria
            <span className="group-hover:translate-x-0.5 transition-transform">→</span>
          </a>
        </div>
      </div>
    </section>
  )
}
